export type LatLng = { lat: number; lng: number };
export type Bounds = {
  ne_lat: number;
  ne_lng: number;
  sw_lat: number;
  sw_lng: number;
};

export type CameraInput = {
  /** URL 검색 박스 (ne/sw). 없으면 null. */
  urlBounds: Bounds | null;
  /** URL t= 강제 이동 신호 유무 */
  hasForceMove: boolean;
  /** URL clat/clng 뷰 복원 좌표. 없으면 null. */
  urlViewCenter: LatLng | null;
  /** 마커 좌표 목록 */
  markers: LatLng[];
  /** 서버가 해석한 지명 중심 (center prop). 유효하지 않으면 null. */
  placeCenter: LatLng | null;
  /** initialZoom prop */
  placeZoom: number | undefined;
};

export type CameraAction =
  | { type: "keep" }
  | { type: "fitBounds"; bounds: Bounds }
  | { type: "fitMarkers"; markers: LatLng[] }
  | { type: "center"; center: LatLng; zoom: number };

export const DEFAULT_PLACE_ZOOM = 12;

/**
 * "이 URL에서 지도를 어디로 보내야 하는가"를 결정하는 순수 함수.
 *
 * 지도 이동이 여러 effect에 흩어져 있으면 같은 렌더에서 두 번 움직이거나,
 * 한쪽이 다른 쪽을 덮어써서 "두 번째 검색부터 안 움직인다" 같은 증상이 난다.
 * 그래서 판단은 전부 여기 한 곳에 모으고, MapView는 결과를 실행만 한다.
 *
 * 결정 순서(먼저 걸리는 규칙이 이긴다):
 * 1. urlViewCenter가 있고 t가 없으면 → keep.
 *    새로고침/뒤로가기 시 보던 위치 복원. 사용자가 pan 하면 view sync가 t를
 *    지우고 clat를 쓰므로 여기 걸린다(= 지도를 되돌리지 않는다).
 * 2. urlBounds가 있고 t가 있으면 → fitBounds(urlBounds). 도시 클릭·내 주변처럼
 *    "이 박스를 보여줘"가 명시된 강제 이동.
 * 3. urlBounds만 있고 t가 없으면 → keep. "이 지역 검색"은 현재 뷰를 유지한다.
 * 4. 마커가 있으면 → fitMarkers(markers). bounds 없이 오는 keyword 검색 경로.
 * 5. placeCenter가 있으면 → center(placeCenter, placeZoom ?? 12). 결과 0건이라
 *    맞출 마커가 없을 때 서버가 해석한 지명 좌표로 이동.
 * 6. 그 외 → keep.
 */
export function decideCamera(input: CameraInput): CameraAction {
  const { urlBounds, hasForceMove, urlViewCenter, markers, placeCenter, placeZoom } =
    input;

  if (urlViewCenter && !hasForceMove) return { type: "keep" };
  if (urlBounds) {
    return hasForceMove
      ? { type: "fitBounds", bounds: urlBounds }
      : { type: "keep" };
  }
  if (markers.length > 0) return { type: "fitMarkers", markers };
  if (placeCenter) {
    return { type: "center", center: placeCenter, zoom: placeZoom ?? DEFAULT_PLACE_ZOOM };
  }
  return { type: "keep" };
}
